import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { useAuth } from '../context/AuthContext';
import usePlan from '../hooks/usePlan';
import Header from '../components/Header';
import Footer from '../components/Footer';

// localStorage helpers
const STORAGE_KEY = 'orcai_orcamentos';

function loadOrcamentos() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
  } catch {
    return [];
  }
}

function saveOrcamentos(list) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
}

function seedDemoData(userId) {
  const existing = loadOrcamentos().filter((o) => o.userId === userId);
  if (existing.length > 0) return;

  const now = Date.now();
  const DAY = 86400000;
  const demo = [
    { id: 'demo-1', userId, nome: 'Casa 120m² - São Paulo', tipo: 'CUB', status: 'completo', valorTotal: 384000, area: 120, estado: 'SP', padrao: 'Médio', criadoEm: new Date(now - 2 * DAY).toISOString(),
      comodos: [{ nome: 'Sala', area: 25 }, { nome: 'Cozinha', area: 15 }, { nome: 'Quarto 1', area: 16 }, { nome: 'Quarto 2', area: 14 }, { nome: 'Banheiro', area: 6 }, { nome: 'Garagem', area: 20 }],
      etapas: [{ nome: 'Fundação', valor: 46080 }, { nome: 'Estrutura', valor: 69120 }, { nome: 'Alvenaria', valor: 42240 }, { nome: 'Cobertura', valor: 30720 }, { nome: 'Instalações elétricas', valor: 34560 }, { nome: 'Instalações hidráulicas', valor: 26880 }, { nome: 'Revestimentos', valor: 57600 }, { nome: 'Esquadrias', valor: 23040 }, { nome: 'Pintura', valor: 26880 }, { nome: 'Acabamentos', valor: 26880 }],
    },
    { id: 'demo-2', userId, nome: 'Reforma Apartamento 80m² - BH', tipo: 'SINAPI', status: 'completo', valorTotal: 196000, area: 80, estado: 'MG', padrao: 'Médio', criadoEm: new Date(now - 5 * DAY).toISOString(),
      comodos: [{ nome: 'Sala/Cozinha integrada', area: 30 }, { nome: 'Suíte', area: 18 }, { nome: 'Quarto', area: 14 }, { nome: 'Banheiro social', area: 5 }, { nome: 'Área de serviço', area: 4 }],
      etapas: [{ nome: 'Demolição', valor: 11760 }, { nome: 'Alvenaria', valor: 23520 }, { nome: 'Instalações elétricas', valor: 27440 }, { nome: 'Instalações hidráulicas', valor: 21560 }, { nome: 'Revestimentos', valor: 43120 }, { nome: 'Esquadrias', valor: 17640 }, { nome: 'Pintura', valor: 19600 }, { nome: 'Acabamentos', valor: 31360 }],
    },
    { id: 'demo-3', userId, nome: 'Galpão Industrial 500m²', tipo: 'SINAPI', status: 'rascunho', valorTotal: null, area: 500, estado: 'PR', padrao: 'Baixo', criadoEm: new Date(now - 10 * DAY).toISOString(), comodos: [], etapas: [] },
    { id: 'demo-4', userId, nome: 'Sobrado Alto Padrão 200m²', tipo: 'CUB', status: 'completo', valorTotal: 920000, area: 200, estado: 'SP', padrao: 'Alto', criadoEm: new Date(now - 15 * DAY).toISOString(),
      comodos: [{ nome: 'Sala de estar', area: 35 }, { nome: 'Sala de jantar', area: 20 }, { nome: 'Cozinha gourmet', area: 22 }, { nome: 'Suíte master', area: 28 }, { nome: 'Suíte 2', area: 18 }, { nome: 'Suíte 3', area: 16 }, { nome: 'Home office', area: 12 }, { nome: 'Lavabo', area: 4 }, { nome: 'Área gourmet', area: 25 }, { nome: 'Garagem 2 vagas', area: 30 }],
      etapas: [{ nome: 'Fundação', valor: 110400 }, { nome: 'Estrutura', valor: 165600 }, { nome: 'Alvenaria', valor: 101200 }, { nome: 'Cobertura', valor: 73600 }, { nome: 'Instalações elétricas', valor: 82800 }, { nome: 'Instalações hidráulicas', valor: 64400 }, { nome: 'Revestimentos', valor: 138000 }, { nome: 'Esquadrias', valor: 64400 }, { nome: 'Pintura', valor: 55200 }, { nome: 'Acabamentos', valor: 64400 }],
    },
    { id: 'demo-5', userId, nome: 'Kitnet 35m² - Curitiba', tipo: 'CUB', status: 'completo', valorTotal: 84000, area: 35, estado: 'PR', padrao: 'Baixo', criadoEm: new Date(now - 20 * DAY).toISOString(),
      comodos: [{ nome: 'Sala/Quarto', area: 18 }, { nome: 'Cozinha', area: 8 }, { nome: 'Banheiro', area: 4 }],
      etapas: [{ nome: 'Fundação', valor: 10080 }, { nome: 'Estrutura', valor: 15120 }, { nome: 'Alvenaria', valor: 9240 }, { nome: 'Cobertura', valor: 6720 }, { nome: 'Instalações elétricas', valor: 7560 }, { nome: 'Instalações hidráulicas', valor: 5880 }, { nome: 'Revestimentos', valor: 12600 }, { nome: 'Esquadrias', valor: 5040 }, { nome: 'Pintura', valor: 5880 }, { nome: 'Acabamentos', valor: 5880 }],
    },
    { id: 'demo-6', userId, nome: 'Casa de Praia 150m² - SC', tipo: 'CUB', status: 'completo', valorTotal: 525000, area: 150, estado: 'SC', padrao: 'Alto', criadoEm: new Date(now - 30 * DAY).toISOString(),
      comodos: [{ nome: 'Sala ampla', area: 40 }, { nome: 'Cozinha', area: 18 }, { nome: 'Suíte 1', area: 22 }, { nome: 'Suíte 2', area: 18 }, { nome: 'Varanda', area: 30 }, { nome: 'Banheiro social', area: 5 }],
      etapas: [{ nome: 'Fundação', valor: 63000 }, { nome: 'Estrutura', valor: 94500 }, { nome: 'Alvenaria', valor: 57750 }, { nome: 'Cobertura', valor: 42000 }, { nome: 'Instalações elétricas', valor: 47250 }, { nome: 'Instalações hidráulicas', valor: 36750 }, { nome: 'Revestimentos', valor: 78750 }, { nome: 'Esquadrias', valor: 36750 }, { nome: 'Pintura', valor: 31500 }, { nome: 'Acabamentos', valor: 36750 }],
    },
    { id: 'demo-7', userId, nome: 'Edícula 40m² - RJ', tipo: 'SINAPI', status: 'completo', valorTotal: 112000, area: 40, estado: 'RJ', padrao: 'Médio', criadoEm: new Date(now - 45 * DAY).toISOString(),
      comodos: [{ nome: 'Sala/Quarto', area: 20 }, { nome: 'Cozinha', area: 8 }, { nome: 'Banheiro', area: 5 }, { nome: 'Área externa', area: 7 }],
      etapas: [{ nome: 'Fundação', valor: 13440 }, { nome: 'Estrutura', valor: 20160 }, { nome: 'Alvenaria', valor: 12320 }, { nome: 'Cobertura', valor: 8960 }, { nome: 'Instalações elétricas', valor: 10080 }, { nome: 'Instalações hidráulicas', valor: 7840 }, { nome: 'Revestimentos', valor: 16800 }, { nome: 'Esquadrias', valor: 6720 }, { nome: 'Pintura', valor: 7840 }, { nome: 'Acabamentos', valor: 7840 }],
    },
  ];

  saveOrcamentos([...loadOrcamentos(), ...demo]);
}

// PDF generation
function generatePDF(orc) {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 14;
  const now = new Date();
  let y = 0;

  // --- Watermark: diagonal "QUANTOCUSTAOBRA" repeated across the page ---
  doc.saveGraphicsState();
  doc.setTextColor(230, 230, 230);
  doc.setFontSize(28);
  doc.setFont('helvetica', 'bold');
  doc.setGState(new doc.GState({ opacity: 0.07 }));
  for (let wy = -50; wy < pageHeight + 100; wy += 60) {
    for (let wx = -80; wx < pageWidth + 100; wx += 140) {
      doc.text('QUANTOCUSTAOBRA', wx, wy, { angle: 45 });
    }
  }
  doc.restoreGraphicsState();

  // Header bar
  doc.setFillColor(30, 58, 95); // navy #1E3A5F
  doc.rect(0, 0, pageWidth, 38, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text('QuantoCustaObra', margin, 16);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text('Orçamento de Obra', margin, 24);
  doc.setFontSize(8);
  doc.text(`Gerado em ${now.toLocaleDateString('pt-BR')}`, margin, 32);

  // Orange accent line
  doc.setFillColor(249, 115, 22); // orange #F97316
  doc.rect(0, 38, pageWidth, 3, 'F');
  y = 52;

  // Project info
  doc.setTextColor(30, 58, 95);
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text(orc.nome, margin, y);
  y += 10;

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100, 100, 100);
  const infoLine = [`Tipo: ${orc.tipo}`, `Padrão: ${orc.padrao}`, `Estado: ${orc.estado}`, `Área: ${orc.area} m²`, `Data: ${new Date(orc.criadoEm).toLocaleDateString('pt-BR')}`].join('  |  ');
  doc.text(infoLine, margin, y);
  y += 12;

  // Total highlight box — navy background with white text
  if (orc.valorTotal) {
    doc.setFillColor(30, 58, 95); // navy #1E3A5F
    doc.roundedRect(margin, y, pageWidth - 2 * margin, 22, 3, 3, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('CUSTO TOTAL ESTIMADO', margin + 6, y + 9);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    const totalStr = orc.valorTotal.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    doc.text(totalStr, pageWidth - margin - 6, y + 15, { align: 'right' });
    y += 30;
  }

  // Rooms table
  if (orc.comodos && orc.comodos.length > 0) {
    doc.setTextColor(30, 58, 95);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Cômodos', margin, y);
    y += 4;

    doc.autoTable({
      startY: y,
      margin: { left: margin, right: margin },
      head: [['Cômodo', 'Área (m²)']],
      body: orc.comodos.map((c) => [c.nome, `${c.area} m²`]),
      theme: 'striped',
      headStyles: { fillColor: [30, 58, 95], fontSize: 9, fontStyle: 'bold' },
      bodyStyles: { fontSize: 9 },
      alternateRowStyles: { fillColor: [245, 247, 250] },
    });
    y = doc.lastAutoTable.finalY + 10;
  }

  // Stages table
  if (orc.etapas && orc.etapas.length > 0) {
    doc.setTextColor(30, 58, 95);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Etapas da Obra', margin, y);
    y += 4;

    doc.autoTable({
      startY: y,
      margin: { left: margin, right: margin },
      head: [['Etapa', 'Valor', '% do Total']],
      body: orc.etapas.map((e) => [
        e.nome,
        e.valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }),
        orc.valorTotal ? `${((e.valor / orc.valorTotal) * 100).toFixed(1)}%` : '—',
      ]),
      foot: [['TOTAL', orc.valorTotal ? orc.valorTotal.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '—', '100%']],
      theme: 'striped',
      headStyles: { fillColor: [30, 58, 95], fontSize: 9, fontStyle: 'bold' },
      bodyStyles: { fontSize: 9 },
      footStyles: { fillColor: [30, 58, 95], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 9 },
      alternateRowStyles: { fillColor: [245, 247, 250] },
    });
    y = doc.lastAutoTable.finalY + 8;
  }

  // Value per m2
  if (orc.valorTotal && orc.area) {
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text(`Custo por m²: ${(orc.valorTotal / orc.area).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}/m²`, margin, y);
    y += 12;
  }

  // Observações section
  doc.setTextColor(30, 58, 95);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text('Observações', margin, y);
  y += 6;
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100, 100, 100);
  const observacoes = [
    '• Valores estimados baseados no CUB/SINAPI vigente.',
    '• Consulte um engenheiro para orçamento executivo.',
    '• Preços sujeitos a variação regional e sazonal.',
  ];
  observacoes.forEach((obs) => {
    doc.text(obs, margin, y);
    y += 5;
  });
  y += 4;

  // Disclaimer
  doc.setFontSize(7);
  doc.setTextColor(150, 150, 150);
  doc.text('Os valores são estimativas baseadas no CUB/SINAPI e não substituem orçamento profissional.', margin, y);

  // Footer with budget number and date+time
  doc.setFillColor(30, 58, 95);
  doc.rect(0, pageHeight - 14, pageWidth, 14, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.text(`quantocustaobra.com.br  |  Orçamento #${orc.id}`, margin, pageHeight - 5);
  doc.text(`Gerado em ${now.toLocaleString('pt-BR')}`, pageWidth - margin, pageHeight - 5, { align: 'right' });

  doc.save(`orcamento_${orc.nome.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().slice(0, 10)}.pdf`);
}

// Helper to format currency as pt-BR string for Excel cells
function fmtCurrencyBR(val) {
  if (val == null) return '';
  return val.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

// Excel generation — two sheets: Resumo + Detalhamento
function generateExcel(orc) {
  const wb = XLSX.utils.book_new();
  const dataStr = new Date(orc.criadoEm).toLocaleDateString('pt-BR');
  const custoM2 = orc.valorTotal && orc.area ? orc.valorTotal / orc.area : null;

  // ========== Sheet 1: Resumo ==========
  const resumoRows = [
    ['QuantoCustaObra — Orçamento de Obra'],
    [],
    ['Informações do Projeto'],
    ['Nome', orc.nome],
    ['Tipo', orc.tipo],
    ['Padrão', orc.padrao],
    ['Estado', orc.estado],
    ['Área (m²)', orc.area],
    ['Data de Criação', dataStr],
    [],
    ['Custo Total', fmtCurrencyBR(orc.valorTotal)],
    ['Custo por m²', custoM2 ? fmtCurrencyBR(custoM2) : ''],
    [],
    ['Resumo das Etapas'],
    ['Etapa', 'Valor (R$)', '% do Total'],
    ...(orc.etapas || []).map((e) => [
      e.nome,
      fmtCurrencyBR(e.valor),
      orc.valorTotal ? `${((e.valor / orc.valorTotal) * 100).toFixed(1)}%` : '',
    ]),
    [],
    ['TOTAL', fmtCurrencyBR(orc.valorTotal), '100%'],
  ];

  const wsResumo = XLSX.utils.aoa_to_sheet(resumoRows);
  wsResumo['!cols'] = [{ wch: 32 }, { wch: 22 }, { wch: 14 }];
  // Merge title row across 3 columns
  wsResumo['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 2 } }];
  XLSX.utils.book_append_sheet(wb, wsResumo, 'Resumo');

  // ========== Sheet 2: Detalhamento ==========
  const detRows = [
    ['QuantoCustaObra — Detalhamento do Orçamento'],
    [],
    ['Projeto:', orc.nome, '', 'Tipo:', orc.tipo],
    ['Padrão:', orc.padrao, '', 'Estado:', orc.estado],
    ['Área:', `${orc.area} m²`, '', 'Data:', dataStr],
    [],
    ['CÔMODOS'],
    ['#', 'Cômodo', 'Área (m²)'],
    ...(orc.comodos || []).map((c, i) => [i + 1, c.nome, `${c.area} m²`]),
  ];

  // blank row before stages
  detRows.push([]);
  detRows.push(['ETAPAS DA OBRA']);
  detRows.push(['#', 'Etapa', 'Valor (R$)', '% do Total']);
  (orc.etapas || []).forEach((e, i) => {
    detRows.push([
      i + 1,
      e.nome,
      fmtCurrencyBR(e.valor),
      orc.valorTotal ? `${((e.valor / orc.valorTotal) * 100).toFixed(1)}%` : '',
    ]);
  });
  detRows.push([]);
  detRows.push(['', 'TOTAL', fmtCurrencyBR(orc.valorTotal), '100%']);

  const wsDet = XLSX.utils.aoa_to_sheet(detRows);
  wsDet['!cols'] = [{ wch: 5 }, { wch: 32 }, { wch: 22 }, { wch: 14 }, { wch: 14 }];
  // Merge title row
  wsDet['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 4 } }];
  XLSX.utils.book_append_sheet(wb, wsDet, 'Detalhamento');

  // Write and save
  const buffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  saveAs(
    new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
    `orcamento_${orc.nome.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().slice(0, 10)}.xlsx`
  );
}

// Insights computation
function computeInsights(list) {
  const completos = list.filter((o) => o.status === 'completo' && o.valorTotal);
  const totalOrcado = completos.reduce((sum, o) => sum + o.valorTotal, 0);
  const estados = [...new Set(list.map((o) => o.estado).filter(Boolean))];
  const cubCount = list.filter((o) => o.tipo === 'CUB').length;
  const sinapiCount = list.filter((o) => o.tipo === 'SINAPI').length;
  const tipoMaisUsado = cubCount >= sinapiCount
    ? { nome: 'CUB', pct: list.length > 0 ? Math.round((cubCount / list.length) * 100) : 0 }
    : { nome: 'SINAPI', pct: list.length > 0 ? Math.round((sinapiCount / list.length) * 100) : 0 };
  const maior = completos.length > 0 ? completos.reduce((a, b) => (a.valorTotal > b.valorTotal ? a : b)) : null;
  const media = completos.length > 0 ? Math.round(totalOrcado / completos.length) : 0;

  // Most productive month
  const monthCounts = {};
  list.forEach((o) => {
    const d = new Date(o.criadoEm);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    monthCounts[key] = (monthCounts[key] || 0) + 1;
  });
  const meses = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
  let mesProdutivo = { label: '—', count: 0 };
  Object.entries(monthCounts).forEach(([key, count]) => {
    if (count > mesProdutivo.count) {
      const [yr, mo] = key.split('-');
      mesProdutivo = { label: `${meses[parseInt(mo) - 1]} ${yr}`, count };
    }
  });

  return { totalOrcado, estados, tipoMaisUsado, maior, media, mesProdutivo, total: list.length, completos: completos.length };
}

function formatCompact(val) {
  if (val >= 1e9) return `R$ ${(val / 1e9).toFixed(1).replace('.', ',')} bi`;
  if (val >= 1e6) return `R$ ${(val / 1e6).toFixed(1).replace('.', ',')} mi`;
  if (val >= 1e3) return `R$ ${(val / 1e3).toFixed(0)} mil`;
  return `R$ ${val}`;
}

function MeusOrcamentos() {
  const { user } = useAuth();
  const { plan } = usePlan();
  const navigate = useNavigate();

  const [orcamentos, setOrcamentos] = useState([]);
  const [filtroTipo, setFiltroTipo] = useState('todos');
  const [ordenar, setOrdenar] = useState('data-desc');
  const [busca, setBusca] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);

  useEffect(() => {
    if (user?.id) {
      seedDemoData(user.id);
      setOrcamentos(loadOrcamentos().filter((o) => o.userId === user.id));
    }
  }, [user?.id]);

  const maxVisible = plan.isFreePlan ? 1 : plan.id === 'basico' ? 10 : Infinity;

  const filtered = useMemo(() => {
    let list = [...orcamentos];
    if (filtroTipo === 'cub') list = list.filter((o) => o.tipo === 'CUB');
    if (filtroTipo === 'sinapi') list = list.filter((o) => o.tipo === 'SINAPI');
    if (busca.trim()) {
      const q = busca.toLowerCase();
      list = list.filter((o) => o.nome.toLowerCase().includes(q));
    }
    if (ordenar === 'data-desc') list.sort((a, b) => new Date(b.criadoEm) - new Date(a.criadoEm));
    if (ordenar === 'data-asc') list.sort((a, b) => new Date(a.criadoEm) - new Date(b.criadoEm));
    if (ordenar === 'valor-desc') list.sort((a, b) => (b.valorTotal || 0) - (a.valorTotal || 0));
    if (ordenar === 'valor-asc') list.sort((a, b) => (a.valorTotal || 0) - (b.valorTotal || 0));
    return list;
  }, [orcamentos, filtroTipo, busca, ordenar]);

  const visible = filtered.slice(0, maxVisible);
  const hiddenCount = Math.max(0, filtered.length - maxVisible);
  const insights = useMemo(() => computeInsights(filtered), [filtered]);

  const handleDuplicate = (orc) => {
    const newOrc = {
      ...orc,
      id: `dup-${Date.now()}`,
      nome: `Cópia de ${orc.nome}`,
      status: 'rascunho',
      criadoEm: new Date().toISOString(),
    };
    const all = loadOrcamentos();
    saveOrcamentos([...all, newOrc]);
    setOrcamentos(loadOrcamentos().filter((o) => o.userId === user.id));
  };

  const handleDelete = (id) => {
    const all = loadOrcamentos().filter((o) => o.id !== id);
    saveOrcamentos(all);
    setOrcamentos(all.filter((o) => o.userId === user.id));
    setConfirmDelete(null);
  };

  const handlePDF = (orc) => {
    if (!plan.canExportPdf) {
      navigate('/planos');
      return;
    }
    generatePDF(orc);
  };

  const handleExcel = (orc) => {
    if (!plan.canExportExcel) {
      navigate('/planos');
      return;
    }
    generateExcel(orc);
  };

  const fmt = (val) => (!val ? '—' : val.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }));
  const fmtDate = (d) => new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
  const filterBtn = (active) => `px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer ${active ? 'bg-primary text-white shadow-sm' : 'bg-card border border-border text-foreground hover:border-primary/50'}`;

  // Impact phrase
  const totalOrcadoFmt = formatCompact(insights.totalOrcado);
  const impactPhrase = insights.total > 0
    ? `${user?.name?.split(' ')[0]}, você já orçou ${totalOrcadoFmt} em ${insights.total} projeto${insights.total > 1 ? 's' : ''} em ${insights.estados.length} estado${insights.estados.length > 1 ? 's' : ''} brasileiro${insights.estados.length > 1 ? 's' : ''}!`
    : 'Comece seu primeiro orçamento e acompanhe suas métricas aqui.';

  return (
    <div className="min-h-screen bg-muted/30">
      <Header />

      <main className="max-w-6xl mx-auto px-4 pt-32 pb-16">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-2">
          <div className="flex items-center gap-4">
            <img src="/images/brand/bruno-principal.png" alt="Bruno" className="w-14 h-14 rounded-full object-cover border-2 border-primary/20 flex-shrink-0" />
            <div>
              <h1 className="text-2xl md:text-3xl font-bold font-display text-secondary">
                Olá, {user?.name?.split(' ')[0]}!
              </h1>
              <p className="text-muted-foreground text-sm">Aqui estão seus orçamentos</p>
            </div>
          </div>
          <Link to="/orcamento/novo" className="no-underline">
            <button className="btn-cta text-sm px-6 py-3 flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Novo Orçamento
            </button>
          </Link>
        </div>

        {/* Impact phrase */}
        <p className="text-sm text-primary font-medium mb-6 ml-[4.5rem] md:ml-[4.5rem]">
          {impactPhrase}
        </p>

        {/* Dashboard Insights */}
        {orcamentos.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <div className="bg-card rounded-2xl border border-border p-4">
              <div className="flex items-center gap-2 mb-1">
                <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                <span className="text-xs text-muted-foreground">Total orçado</span>
              </div>
              <div className="text-lg font-extrabold text-secondary">{formatCompact(insights.totalOrcado)}</div>
            </div>
            <div className="bg-card rounded-2xl border border-border p-4">
              <div className="flex items-center gap-2 mb-1">
                <svg className="w-4 h-4 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                <span className="text-xs text-muted-foreground">Orçamentos</span>
              </div>
              <div className="text-lg font-extrabold text-secondary">{insights.total} <span className="text-xs font-normal text-muted-foreground">({insights.completos} completos)</span></div>
            </div>
            <div className="bg-card rounded-2xl border border-border p-4">
              <div className="flex items-center gap-2 mb-1">
                <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                <span className="text-xs text-muted-foreground">Estados</span>
              </div>
              <div className="text-lg font-extrabold text-secondary">{insights.estados.length} <span className="text-xs font-normal text-muted-foreground">{insights.estados.join(', ')}</span></div>
            </div>
            <div className="bg-card rounded-2xl border border-border p-4">
              <div className="flex items-center gap-2 mb-1">
                <svg className="w-4 h-4 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg>
                <span className="text-xs text-muted-foreground">Tipo mais usado</span>
              </div>
              <div className="text-lg font-extrabold text-secondary">{insights.tipoMaisUsado.nome} <span className="text-xs font-normal text-muted-foreground">({insights.tipoMaisUsado.pct}%)</span></div>
            </div>
            <div className="bg-card rounded-2xl border border-border p-4">
              <div className="flex items-center gap-2 mb-1">
                <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" /></svg>
                <span className="text-xs text-muted-foreground">Maior orçamento</span>
              </div>
              <div className="text-lg font-extrabold text-primary">{insights.maior ? fmt(insights.maior.valorTotal) : '—'}</div>
              {insights.maior && <div className="text-xs text-muted-foreground truncate">{insights.maior.nome}</div>}
            </div>
            <div className="bg-card rounded-2xl border border-border p-4">
              <div className="flex items-center gap-2 mb-1">
                <svg className="w-4 h-4 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>
                <span className="text-xs text-muted-foreground">Média por orçamento</span>
              </div>
              <div className="text-lg font-extrabold text-secondary">{formatCompact(insights.media)}</div>
            </div>
            <div className="bg-card rounded-2xl border border-border p-4">
              <div className="flex items-center gap-2 mb-1">
                <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                <span className="text-xs text-muted-foreground">Mês mais produtivo</span>
              </div>
              <div className="text-lg font-extrabold text-secondary">{insights.mesProdutivo.label}</div>
              <div className="text-xs text-muted-foreground">{insights.mesProdutivo.count} orçamento{insights.mesProdutivo.count > 1 ? 's' : ''}</div>
            </div>
            {/* Share button - Pro only */}
            <div className="bg-card rounded-2xl border border-border p-4 flex flex-col justify-center items-center">
              {plan.id === 'pro' ? (
                <button
                  onClick={() => {
                    const text = `Já orçei ${formatCompact(insights.totalOrcado)} em ${insights.total} projetos com o QuantoCustaObra! 🏗️`;
                    if (navigator.share) {
                      navigator.share({ title: 'Meu portfólio QuantoCustaObra', text });
                    } else {
                      navigator.clipboard.writeText(text);
                      alert('Texto copiado para a área de transferência!');
                    }
                  }}
                  className="flex flex-col items-center gap-1 cursor-pointer bg-transparent border-none"
                >
                  <svg className="w-6 h-6 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" /></svg>
                  <span className="text-xs font-medium text-accent">Compartilhar</span>
                  <span className="text-[10px] text-muted-foreground">portfólio</span>
                </button>
              ) : (
                <div className="text-center">
                  <svg className="w-6 h-6 text-muted-foreground/40 mx-auto mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
                  <span className="text-[10px] text-muted-foreground">Compartilhar<br />Plano Pro</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Plan banner */}
        {plan.isFreePlan && (
          <div className="bg-primary/5 border border-primary/20 rounded-2xl p-4 mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <svg className="w-5 h-5 text-primary flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              <p className="text-sm text-foreground"><strong>Plano Gratuito:</strong> Veja apenas seu último orçamento. Faça upgrade para acessar todos e exportar PDF/Excel.</p>
            </div>
            <Link to="/planos" className="no-underline flex-shrink-0">
              <button className="text-sm font-semibold text-primary hover:text-primary/80 transition-colors cursor-pointer bg-transparent border-none">Ver planos →</button>
            </Link>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="flex gap-2 flex-wrap">
            <button onClick={() => setFiltroTipo('todos')} className={filterBtn(filtroTipo === 'todos')}>Todos</button>
            <button onClick={() => setFiltroTipo('cub')} className={filterBtn(filtroTipo === 'cub')}>CUB</button>
            <button onClick={() => setFiltroTipo('sinapi')} className={filterBtn(filtroTipo === 'sinapi')}>SINAPI</button>
          </div>
          <select value={ordenar} onChange={(e) => setOrdenar(e.target.value)} className="px-4 py-2 rounded-full text-sm border border-border bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary cursor-pointer">
            <option value="data-desc">Mais recentes</option>
            <option value="data-asc">Mais antigos</option>
            <option value="valor-desc">Maior valor</option>
            <option value="valor-asc">Menor valor</option>
          </select>
          <div className="relative flex-1 min-w-[200px]">
            <input type="text" placeholder="Buscar por nome do projeto..." value={busca} onChange={(e) => setBusca(e.target.value)} className="w-full px-4 py-2 pl-10 rounded-full text-sm border border-border bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary" />
            <svg className="absolute left-3 top-2.5 w-4 h-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
          </div>
        </div>

        {/* Cards */}
        {visible.length === 0 ? (
          <div className="bg-card rounded-2xl shadow-card p-12 text-center">
            <svg className="w-16 h-16 mx-auto text-muted-foreground/40 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
            <h3 className="text-lg font-bold text-secondary font-display mb-2">Nenhum orçamento encontrado</h3>
            <p className="text-muted-foreground text-sm mb-6">{busca || filtroTipo !== 'todos' ? 'Tente alterar os filtros ou a busca.' : 'Comece criando seu primeiro orçamento!'}</p>
            {!busca && filtroTipo === 'todos' && (
              <Link to="/orcamento/novo" className="no-underline"><button className="btn-cta text-sm px-6 py-3">Criar Primeiro Orçamento</button></Link>
            )}
          </div>
        ) : (
          <div className="grid gap-4">
            {visible.map((orc) => (
              <div key={orc.id} className="bg-card rounded-2xl border border-border shadow-card hover:shadow-elevated transition-all duration-300 p-5 md:p-6">
                <div className="flex flex-col md:flex-row md:items-center gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <h3 className="text-base font-bold text-secondary font-display truncate">{orc.nome}</h3>
                      <span className={`text-xs font-bold px-2.5 py-0.5 rounded-full flex-shrink-0 ${orc.tipo === 'CUB' ? 'bg-accent/10 text-accent' : 'bg-primary/10 text-primary'}`}>{orc.tipo}</span>
                      <span className={`text-xs font-medium px-2.5 py-0.5 rounded-full flex-shrink-0 ${orc.status === 'completo' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>{orc.status === 'completo' ? 'Completo' : 'Rascunho'}</span>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <span>{fmtDate(orc.criadoEm)}</span>
                      {orc.area && <span>{orc.area} m²</span>}
                      {orc.estado && <span>{orc.estado}</span>}
                      {orc.padrao && <span>Padrão {orc.padrao}</span>}
                    </div>
                  </div>
                  <div className="md:text-right flex-shrink-0">
                    <div className="text-xl md:text-2xl font-extrabold text-secondary">{fmt(orc.valorTotal)}</div>
                    {orc.valorTotal && orc.area && <div className="text-xs text-muted-foreground">{fmt(orc.valorTotal / orc.area)}/m²</div>}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {orc.status === 'completo' && (
                      <button onClick={() => handlePDF(orc)} title="Baixar PDF" className="p-2.5 rounded-xl bg-muted hover:bg-primary/10 text-muted-foreground hover:text-primary transition-colors cursor-pointer">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                      </button>
                    )}
                    {orc.status === 'completo' && (
                      <button onClick={() => handleExcel(orc)} title={plan.canExportExcel ? 'Baixar Excel' : 'Excel — Plano Profissional'} disabled={!plan.canExportExcel} className="p-2.5 rounded-xl bg-muted hover:bg-green-100 text-muted-foreground hover:text-green-700 transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                      </button>
                    )}
                    {orc.status === 'rascunho' && (
                      <button onClick={() => { localStorage.setItem('orcai_cub_prefill', JSON.stringify({ estado: orc.estado, areaConstruida: String(orc.area || '') })); navigate('/orcamento-cub?from=analise'); }} title="Editar orçamento" className="p-2.5 rounded-xl bg-muted hover:bg-accent/10 text-muted-foreground hover:text-accent transition-colors cursor-pointer">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                      </button>
                    )}
                    <button onClick={() => handleDuplicate(orc)} title="Duplicar orçamento" className="p-2.5 rounded-xl bg-muted hover:bg-accent/10 text-muted-foreground hover:text-accent transition-colors cursor-pointer">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                    </button>
                    {confirmDelete === orc.id ? (
                      <div className="flex items-center gap-1">
                        <button onClick={() => handleDelete(orc.id)} className="px-3 py-1.5 rounded-lg bg-destructive text-white text-xs font-medium cursor-pointer hover:bg-destructive/90">Confirmar</button>
                        <button onClick={() => setConfirmDelete(null)} className="px-3 py-1.5 rounded-lg bg-muted text-foreground text-xs font-medium cursor-pointer">Cancelar</button>
                      </div>
                    ) : (
                      <button onClick={() => setConfirmDelete(orc.id)} title="Excluir" className="p-2.5 rounded-xl bg-muted hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors cursor-pointer">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {hiddenCount > 0 && (
          <div className="mt-6 bg-card rounded-2xl border border-primary/20 p-6 text-center">
            <p className="text-sm text-foreground mb-3"><strong>+{hiddenCount} orçamento{hiddenCount > 1 ? 's' : ''}</strong> oculto{hiddenCount > 1 ? 's' : ''} pelo seu plano atual ({plan.label}).</p>
            <Link to="/planos" className="no-underline"><button className="btn-cta text-sm px-6 py-2.5">Fazer Upgrade</button></Link>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}

export default MeusOrcamentos;
